import { useEffect, useState } from 'react';

declare global {
  interface Window {
    usertour?: {
      init: (token: string) => void;
      identify: (
        userId: string,
        userProperties?: Record<string, unknown>
      ) => void;
      identifyAnonymous: () => void;
      isIdentified: () => boolean;
      updateUser: (properties: Record<string, unknown>) => void;
      reset: () => void;
      group: (
        groupId: string,
        groupProperties?: Record<string, unknown>
      ) => void;
      updateGroup: (properties: Record<string, unknown>) => void;
      start: () => void;
      isStarted: () => boolean;
      endAll: () => void;
      setBaseZIndex: (zIndex: number) => void;
      setCustomNavigate: (navigate: (url: string) => void) => void;
    };
  }
}

export interface UserTourConfig {
  token: string;
  userId?: string;
  userProperties?: Record<string, unknown>;
  groupId?: string;
  groupProperties?: Record<string, unknown>;
}

function isPromiseLike(value: unknown): value is PromiseLike<unknown> {
  return (
    !!value &&
    (typeof value === 'object' || typeof value === 'function') &&
    typeof (value as { then?: unknown }).then === 'function'
  );
}

class UserTourGuide {
  private isInitialized = false;
  private config: UserTourConfig;

  constructor(
    config: UserTourConfig,
    private logger = console
  ) {
    this.config = config;
  }

  // Wraps a call into the underlying SDK so that both synchronous throws and
  // rejected promises are logged instead of escaping as unhandled rejections.
  private safeCall(action: string, fn: () => unknown): void {
    try {
      const result = fn();
      if (isPromiseLike(result)) {
        Promise.resolve(result).catch((error) => {
          this.logger.error(`Failed to ${action}:`, error);
        });
      }
    } catch (error) {
      this.logger.error(`Failed to ${action}:`, error);
    }
  }

  async init(): Promise<void> {
    if (this.isInitialized || typeof window === 'undefined') {
      return;
    }

    try {
      // Dynamically import UserTour.js
      await import('usertour.js');

      if (window.usertour) {
        // Initialize with your Usertour.js Token
        window.usertour.init(this.config.token);
        this.isInitialized = true;
      }
    } catch (error) {
      this.logger.error('Failed to initialize UserTour.js:', error);
    }
  }

  // User identification methods
  identify(userId: string, userProperties?: Record<string, unknown>): void {
    if (
      typeof window === 'undefined' ||
      !window.usertour ||
      !this.isInitialized
    ) {
      this.logger.warn('UserTour.js not initialized');
      return;
    }

    this.safeCall('identify user', () =>
      window.usertour?.identify(userId, userProperties)
    );
  }

  identifyAnonymous(): void {
    if (
      typeof window === 'undefined' ||
      !window.usertour ||
      !this.isInitialized
    ) {
      this.logger.warn('UserTour.js not initialized');
      return;
    }

    this.safeCall('identify anonymous user', () =>
      window.usertour?.identifyAnonymous()
    );
  }

  isIdentified(): boolean {
    if (
      typeof window === 'undefined' ||
      !window.usertour ||
      !this.isInitialized
    ) {
      return false;
    }

    try {
      return window.usertour.isIdentified();
    } catch (error) {
      this.logger.error('Failed to check if user is identified:', error);
      return false;
    }
  }

  updateUser(properties: Record<string, unknown>): void {
    if (
      typeof window === 'undefined' ||
      !window.usertour ||
      !this.isInitialized
    ) {
      this.logger.warn('UserTour.js not initialized');
      return;
    }

    this.safeCall('update user properties', () =>
      window.usertour?.updateUser(properties)
    );
  }

  reset(): void {
    if (
      typeof window === 'undefined' ||
      !window.usertour ||
      !this.isInitialized
    ) {
      this.logger.warn('UserTour.js not initialized');
      return;
    }

    this.safeCall('reset UserTour.js', () => window.usertour?.reset());
  }

  // Company/Group methods
  group(groupId: string, groupProperties?: Record<string, unknown>): void {
    if (
      typeof window === 'undefined' ||
      !window.usertour ||
      !this.isInitialized
    ) {
      this.logger.warn('UserTour.js not initialized');
      return;
    }

    this.safeCall('associate group', () =>
      window.usertour?.group(groupId, groupProperties)
    );
  }

  updateGroup(properties: Record<string, unknown>): void {
    if (
      typeof window === 'undefined' ||
      !window.usertour ||
      !this.isInitialized
    ) {
      this.logger.warn('UserTour.js not initialized');
      return;
    }

    this.safeCall('update group properties', () =>
      window.usertour?.updateGroup(properties)
    );
  }

  // Content methods
  start(): void {
    if (
      typeof window === 'undefined' ||
      !window.usertour ||
      !this.isInitialized
    ) {
      this.logger.warn('UserTour.js not initialized');
      return;
    }

    this.safeCall('start UserTour.js content', () => window.usertour?.start());
  }

  isStarted(): boolean {
    if (
      typeof window === 'undefined' ||
      !window.usertour ||
      !this.isInitialized
    ) {
      return false;
    }

    try {
      return window.usertour.isStarted();
    } catch (error) {
      this.logger.error('Failed to check if UserTour.js is started:', error);
      return false;
    }
  }

  endAll(): void {
    if (
      typeof window === 'undefined' ||
      !window.usertour ||
      !this.isInitialized
    ) {
      this.logger.warn('UserTour.js not initialized');
      return;
    }

    this.safeCall('end UserTour.js content', () => window.usertour?.endAll());
  }

  // Advanced methods
  setBaseZIndex(zIndex: number): void {
    if (
      typeof window === 'undefined' ||
      !window.usertour ||
      !this.isInitialized
    ) {
      this.logger.warn('UserTour.js not initialized');
      return;
    }

    try {
      window.usertour.setBaseZIndex(zIndex);
    } catch (error) {
      this.logger.error('Failed to set base Z-index:', error);
    }
  }

  setCustomNavigate(navigate: (url: string) => void): void {
    if (
      typeof window === 'undefined' ||
      !window.usertour ||
      !this.isInitialized
    ) {
      this.logger.warn('UserTour.js not initialized');
      return;
    }

    try {
      window.usertour.setCustomNavigate(navigate);
    } catch (error) {
      this.logger.error('Failed to set custom navigate function:', error);
    }
  }
}

// Global instance
let userTourInstance: UserTourGuide | null = null;

export function initializeUserTour(
  config: UserTourConfig,
  logger = console
): UserTourGuide {
  if (!userTourInstance) {
    userTourInstance = new UserTourGuide(config, logger);
  }
  return userTourInstance;
}

// React hook for UserTour
export function useUserTour(config?: UserTourConfig, logger = console) {
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (config && !userTourInstance) {
      userTourInstance = new UserTourGuide(config, logger);
    }

    if (userTourInstance) {
      userTourInstance.init().then(() => {
        setIsInitialized(true);
      });
    }
  }, [config, logger]);

  return {
    isInitialized,
    // User methods
    identify: (userId: string, userProperties?: Record<string, unknown>) => {
      userTourInstance?.identify(userId, userProperties);
    },
    identifyAnonymous: () => {
      userTourInstance?.identifyAnonymous();
    },
    isIdentified: () => {
      return userTourInstance?.isIdentified() || false;
    },
    updateUser: (properties: Record<string, unknown>) => {
      userTourInstance?.updateUser(properties);
    },
    reset: () => {
      userTourInstance?.reset();
    },
    // Group methods
    group: (groupId: string, groupProperties?: Record<string, unknown>) => {
      userTourInstance?.group(groupId, groupProperties);
    },
    updateGroup: (properties: Record<string, unknown>) => {
      userTourInstance?.updateGroup(properties);
    },
    // Content methods
    start: () => {
      userTourInstance?.start();
    },
    isStarted: () => {
      return userTourInstance?.isStarted() || false;
    },
    endAll: () => {
      userTourInstance?.endAll();
    },
    // Advanced methods
    setBaseZIndex: (zIndex: number) => {
      userTourInstance?.setBaseZIndex(zIndex);
    },
    setCustomNavigate: (navigate: (url: string) => void) => {
      userTourInstance?.setCustomNavigate(navigate);
    },
  };
}
